import type { V2 } from '../core/math';
import type { NavGrid } from '../sim/navGrid';
import type { ZoneLayout } from '../zones/types';

export interface MinimapIcon {
  x: number;
  z: number;
  kind: 'player' | 'ally' | 'monster' | 'elite' | 'boss' | 'npc' | 'quest' | 'portal' | 'waystone' | 'gather' | 'seed' | 'chest' | 'objective';
  facing?: number;
}

const PX_PER_M = 2;

/**
 * Square, translucent minimap: the zone is rasterised once from the nav grid
 * and terrain (walkable areas in warm khaki with gold edges, water teal,
 * blocked dark), then icons are drawn each frame around the player.
 */
export class Minimap {
  readonly canvas = document.createElement('canvas');
  private bg: HTMLCanvasElement | null = null;
  private layout: ZoneLayout | null = null;
  zoom = 1;

  setZone(layout: ZoneLayout, nav: NavGrid): void {
    this.layout = layout;
    const w = Math.ceil(layout.width * PX_PER_M);
    const h = Math.ceil(layout.depth * PX_PER_M);
    const bg = document.createElement('canvas');
    bg.width = w;
    bg.height = h;
    const g = bg.getContext('2d');
    if (!g) return;
    const img = g.createImageData(w, h);
    const sheet = layout.water.find((wr) => wr.shape.kind === 'sheet');
    for (let py = 0; py < h; py++) {
      for (let px = 0; px < w; px++) {
        const x = layout.minX + px / PX_PER_M;
        const z = layout.minZ + py / PX_PER_M;
        const i = (py * w + px) * 4;
        const walk = nav.walkable({ x, z });
        let r = 18;
        let gg = 20;
        let b = 20;
        let a = 150;
        if (sheet && layout.heightAt(x, z) < sheet.level) {
          r = 40;
          gg = 90;
          b = 100;
          a = 190;
        } else if (walk) {
          const s = layout.surfaceAt(x, z);
          const sum = s[0] + s[1] + s[2] + s[3] || 1;
          const road = s[1] / sum;
          r = 112 + road * 40;
          gg = 106 + road * 30;
          b = 78 + road * 20;
          a = 170;
        }
        img.data[i] = r;
        img.data[i + 1] = gg;
        img.data[i + 2] = b;
        img.data[i + 3] = a;
      }
    }
    g.putImageData(img, 0, 0);
    // Gold outline around walkable space.
    g.globalCompositeOperation = 'source-over';
    const outline = g.getImageData(0, 0, w, h);
    const out = g.createImageData(w, h);
    for (let py = 1; py < h - 1; py++) {
      for (let px = 1; px < w - 1; px++) {
        const i = (py * w + px) * 4;
        const me = (outline.data[i + 3] ?? 0) >= 170;
        const n = (outline.data[i - 4 + 3] ?? 0) >= 170 && (outline.data[i + 4 + 3] ?? 0) >= 170 && (outline.data[i - w * 4 + 3] ?? 0) >= 170 && (outline.data[i + w * 4 + 3] ?? 0) >= 170;
        if (me && !n) {
          out.data[i] = 214;
          out.data[i + 1] = 178;
          out.data[i + 2] = 96;
          out.data[i + 3] = 230;
        }
      }
    }
    const tmp = document.createElement('canvas');
    tmp.width = w;
    tmp.height = h;
    tmp.getContext('2d')?.putImageData(out, 0, 0);
    g.drawImage(tmp, 0, 0);
    this.bg = bg;
  }

  draw(center: V2, icons: MinimapIcon[]): void {
    const c = this.canvas;
    const rect = c.getBoundingClientRect();
    const W = Math.max(64, Math.round(rect.width * devicePixelRatio));
    const H = Math.max(64, Math.round(rect.height * devicePixelRatio));
    if (c.width !== W || c.height !== H) {
      c.width = W;
      c.height = H;
    }
    const g = c.getContext('2d');
    if (!g || !this.layout || !this.bg) return;
    g.clearRect(0, 0, W, H);
    const scale = (W / 90) * this.zoom; // ~90 m across at zoom 1
    const L = this.layout;
    const toScreen = (x: number, z: number): [number, number] => [W / 2 + (x - center.x) * scale, H / 2 + (z - center.z) * scale];
    const [ox, oy] = toScreen(L.minX, L.minZ);
    g.imageSmoothingEnabled = true;
    g.drawImage(this.bg, ox, oy, L.width * scale, L.depth * scale);

    for (const ic of icons) {
      const [x, y] = toScreen(ic.x, ic.z);
      if (x < -10 || y < -10 || x > W + 10 || y > H + 10) continue;
      const s = devicePixelRatio;
      switch (ic.kind) {
        case 'monster':
          dot(g, x, y, 2.4 * s, '#e0443a');
          break;
        case 'elite':
          dot(g, x, y, 3.4 * s, '#ff7a3a', '#200');
          break;
        case 'boss':
          dot(g, x, y, 6 * s, '#ff2a2a', '#fff');
          break;
        case 'npc':
          dot(g, x, y, 3 * s, '#f6e7a6', '#000');
          break;
        case 'ally':
          dot(g, x, y, 3.2 * s, '#5ad06a', '#000');
          break;
        case 'quest':
          g.font = `700 ${14 * s}px Cinzel, serif`;
          g.fillStyle = '#ffd84a';
          g.textAlign = 'center';
          g.fillText('!', x, y + 5 * s);
          break;
        case 'objective':
          ring(g, x, y, 7 * s, '#ffd84a');
          break;
        case 'portal':
          ring(g, x, y, 5 * s, '#7ac8ff');
          break;
        case 'waystone':
          diamond(g, x, y, 5 * s, '#7ad8ff');
          break;
        case 'gather':
          dot(g, x, y, 2.2 * s, '#9be07a');
          break;
        case 'seed':
          dot(g, x, y, 1.8 * s, '#8dffb0');
          break;
        case 'chest':
          diamond(g, x, y, 3.5 * s, '#ffd070');
          break;
        case 'player': {
          g.save();
          g.translate(x, y);
          g.rotate(Math.PI - (ic.facing ?? 0));
          g.beginPath();
          g.arc(0, 0, 7 * s, 0, Math.PI * 2);
          g.strokeStyle = '#3ac0b8';
          g.lineWidth = 1.5 * s;
          g.stroke();
          g.beginPath();
          g.moveTo(0, -6 * s);
          g.lineTo(4 * s, 5 * s);
          g.lineTo(0, 2.5 * s);
          g.lineTo(-4 * s, 5 * s);
          g.closePath();
          g.fillStyle = '#6aff7a';
          g.fill();
          g.restore();
          break;
        }
      }
    }
  }
}

function dot(g: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string, stroke?: string): void {
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fillStyle = fill;
  g.fill();
  if (stroke) {
    g.lineWidth = 1;
    g.strokeStyle = stroke;
    g.stroke();
  }
}
function ring(g: CanvasRenderingContext2D, x: number, y: number, r: number, stroke: string): void {
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.lineWidth = 2;
  g.strokeStyle = stroke;
  g.stroke();
}
function diamond(g: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string): void {
  g.beginPath();
  g.moveTo(x, y - r);
  g.lineTo(x + r, y);
  g.lineTo(x, y + r);
  g.lineTo(x - r, y);
  g.closePath();
  g.fillStyle = fill;
  g.fill();
}
